import {
  PersistedClient,
  Persister,
} from "@tanstack/react-query-persist-client";
import { get, set, del } from "idb-keyval";

/**
 * How long we let writes pile up before flushing the newest one.
 *
 * This is the maximum age of the persisted cache if the tab dies without
 * warning; every ordinary way of leaving the page flushes first (see
 * `createCoalescingWriter`). Losing the last few seconds only means those
 * queries refetch, so we trade a little freshness for a lot of main thread.
 */
const PERSIST_INTERVAL_MS = 5000;

type CoalescingWriter = {
  enqueue: (client: PersistedClient) => void;
  cancel: () => void;
  flushPending: () => void;
};

/**
 * The writers currently holding a write that has not been flushed yet.
 *
 * This exists so the whole module needs a single `pagehide` listener instead of
 * one per writer. A writer is built inside a `useMemo` in `TrpcWrapper`, and a
 * `useMemo` factory can run more than once for a single mount — StrictMode
 * double-renders in development — so a listener registered in the writer's own
 * constructor could outlive the writer that owns it, with nothing able to remove
 * it: react-query's `Persister` interface has no teardown hook to hang a
 * `removeEventListener` off.
 *
 * Membership tracks the pending write rather than the writer's lifetime. A
 * writer joins when it takes a write and leaves as soon as that write is flushed
 * or dropped, so a writer nothing is ever persisted through — the discarded half
 * of a double-invoked memo — never joins at all and this cannot accumulate.
 */
const pendingWriters = new Set<CoalescingWriter>();

if (typeof globalThis.addEventListener === "function") {
  /**
   * Don't throw away the newest cache just because the user left. This is
   * best-effort — IndexedDB writes are asynchronous, so a page that is torn
   * down immediately may not finish the transaction — but it costs one listener
   * and it turns "the last few seconds are always lost" into "rarely lost".
   */
  globalThis.addEventListener("pagehide", () => {
    for (const writer of pendingWriters) writer.flushPending();
  });
}

/**
 * Drops each query's `meta` before the snapshot reaches IndexedDB.
 *
 * Why: the persist client is v5, and v5's `dehydrate` copies `query.meta` into
 * the snapshot (v4's never did). Since `meta.onError` replaced the per-query
 * `onError` option, `meta` routinely holds functions, and a single one makes
 * `IDBObjectStore.put` throw `DataCloneError` — failing the write for the
 * *whole* cache, not just that query. Nothing is lost by dropping it: `meta` is
 * query config, and `useQuery` passes it again when the query mounts.
 */
export function stripQueryMeta(client: PersistedClient): PersistedClient {
  return {
    ...client,
    clientState: {
      ...client.clientState,
      queries: client.clientState.queries.map((query) => {
        const withoutMeta = { ...query };
        delete withoutMeta.meta;
        return withoutMeta;
      }),
    },
  };
}

/**
 * Coalesces persist writes so at most one lands per `PERSIST_INTERVAL_MS`.
 *
 * Why this exists: `@tanstack/react-query-persist-client` v5 removed removed `throttleTime`, and
 * `persistQueryClientSubscribe` now calls `persistQueryClientSave` on *every*
 * added/removed/updated query cache event. Each save hands the whole dehydrated
 * cache to `idb-keyval`'s `set`, and `IDBObjectStore.put` structure-clones that
 * value **synchronously on the main thread**. So the cost of persisting is
 * (number of cache events) × (size of the entire cache) — both of which grow
 * together as an app loads more data. *
 */
function createCoalescingWriter(
  write: (client: PersistedClient) => Promise<void>,
): CoalescingWriter {
  let pending: PersistedClient | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let lastFlushedAt = 0;

  const clearTimer = () => {
    if (timer !== undefined) {
      clearTimeout(timer);
      timer = undefined;
    }
  };

  const flush = () => {
    const client = pending;
    pending = undefined;
    lastFlushedAt = Date.now();
    pendingWriters.delete(writer);
    if (client) void write(stripQueryMeta(client));
  };

  const scheduleFlush = () => {
    if (timer !== undefined) return; // a flush is already queued
    const wait = Math.max(
      0,
      PERSIST_INTERVAL_MS - (Date.now() - lastFlushedAt),
    );
    timer = setTimeout(() => {
      timer = undefined;
      flush();
    }, wait);
  };

  const writer: CoalescingWriter = {
    enqueue: (client: PersistedClient) => {
      pending = client;
      pendingWriters.add(writer);
      scheduleFlush();
    },

    cancel: () => {
      pending = undefined;
      pendingWriters.delete(writer);
      clearTimer();
    },

    /**
     * Write immediately instead of waiting for the interval. Only the
     * `pagehide` listener needs this — everything else goes through the
     * coalescing path.
     */
    flushPending: () => {
      clearTimer();
      flush();
    },
  };

  return writer;
}

/**
 * Type guard to check if an error is a NotFoundError from idb-keyval
 * indicating a missing IndexedDB object store.
 */
function isMissingObjectStoreError(error: unknown): boolean {
  return (
    error instanceof DOMException &&
    error.name === "NotFoundError" &&
    error.message.includes("is not a known object store name")
  );
}

/**
 * Creates a robust IndexedDB-based persister for React Query that handles
 * missing object stores gracefully without throwing errors.
 */
export function createRobustIDBPersister(
  idbValidKey: IDBValidKey = "REACT_QUERY_OFFLINE_CACHE",
): Persister {
  const writer = createCoalescingWriter(async (client: PersistedClient) => {
    try {
      await set(idbValidKey, client);
    } catch (error) {
      if (isMissingObjectStoreError(error)) {
        // Normal on first run - skip persistence to avoid breaking the app
        if (import.meta.env.MODE === "production") {
          console.warn("Failed to persist query cache to IndexedDB:", error);
        } else {
          console.debug(
            "Skipping query cache persistence - IndexedDB object store not yet ready",
            { key: idbValidKey },
          );
        }
        return;
      }

      // Log other errors but don't break the app
      if (import.meta.env.MODE === "production") {
        console.warn("Failed to persist query cache to IndexedDB:", error);
      } else {
        console.error("Failed to persist query cache to IndexedDB:", error);
      }
    }
  });

  return {
    /**
     * Resolves as soon as the write is queued rather than when it lands.
     * Nothing awaits the persisted cache — react-query calls this purely as a
     * side effect — so there is nothing to report back.
     */
    persistClient: (client: PersistedClient) => {
      writer.enqueue(client);
      return Promise.resolve();
    },

    restoreClient: async () => {
      try {
        return await get<PersistedClient>(idbValidKey);
      } catch (error) {
        if (isMissingObjectStoreError(error)) {
          // Normal on first run - return undefined (no cached data)
          if (import.meta.env.MODE === "production") {
            console.warn(
              "Failed to restore query cache from IndexedDB:",
              error,
            );
          } else {
            console.debug(
              "No query cache found in IndexedDB (object store not yet initialized)",
              { key: idbValidKey },
            );
          }
          return;
        }

        // Log other errors but don't break the app
        if (import.meta.env.MODE === "production") {
          console.warn("Failed to restore query cache from IndexedDB:", error);
        } else {
          console.error("Failed to restore query cache from IndexedDB:", error);
        }
      }
    },

    removeClient: async () => {
      // Drop any queued write, or it would resurrect the cache we just discarded.
      writer.cancel();
      try {
        await del(idbValidKey);
      } catch (error) {
        if (isMissingObjectStoreError(error)) {
          // Normal if there's nothing to remove
          if (import.meta.env.MODE === "production") {
            console.warn("Failed to remove query cache from IndexedDB:", error);
          } else {
            console.debug(
              "Nothing to remove from IndexedDB query cache (object store not yet initialized)",
              { key: idbValidKey },
            );
          }
          return;
        }

        // Log other errors but don't break the app
        if (import.meta.env.MODE === "production") {
          console.warn("Failed to remove query cache from IndexedDB:", error);
        } else {
          console.error("Failed to remove query cache from IndexedDB:", error);
        }
      }
    },
  };
}

/**
 * Original IndexedDB-based persister for React Query (kept for comparison/testing).
 * This version does not handle NotFoundError exceptions and will throw if the
 * object store doesn't exist.
 */
export function createOriginalIDBPersister(
  idbValidKey: IDBValidKey = "REACT_QUERY_OFFLINE_CACHE",
): Persister {
  const writer = createCoalescingWriter(async (client: PersistedClient) => {
    await set(idbValidKey, client);
  });

  return {
    persistClient: (client: PersistedClient) => {
      writer.enqueue(client);
      return Promise.resolve();
    },
    restoreClient: async () => {
      return await get<PersistedClient>(idbValidKey);
    },
    removeClient: async () => {
      writer.cancel();
      await del(idbValidKey);
    },
  };
}

/**
 * Factory function that creates the appropriate IndexedDB persister based on feature flags.
 */
export function createIDBPersister(
  idbValidKey: IDBValidKey = "REACT_QUERY_OFFLINE_CACHE",
): Persister {
  // Defaults to robust version; TrpcWrapper.tsx handles feature flag logic
  return createRobustIDBPersister(idbValidKey);
}
